import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Brain, TrendingUp, TrendingDown, Users, Target, BarChart3,
  PieChart, MapPin, Lightbulb, RefreshCw, Sparkles, ArrowUp, ArrowDown,
  CheckCircle2, AlertTriangle, Clock, DollarSign
} from "lucide-react";
import api from "../api";

const AIInsightsPage = () => {
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [segments, setSegments] = useState([]);
  const [selectedSegments, setSelectedSegments] = useState([]);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [timePeriod, setTimePeriod] = useState("90");

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, segRes] = await Promise.all([
        api.get("/ai-analysis/dashboard/summary"),
        api.get("/segments")
      ]);
      setDashboardData(dashRes.data);
      setSegments(segRes.data?.segments || segRes.data || []);
    } catch (error) {
      console.error("Error fetching dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch trends
  const fetchTrends = useCallback(async () => {
    try {
      const response = await api.post("/ai-analysis/trends", {
        metrics: ["revenue", "lead_count", "conversion_rate"],
        time_period_days: parseInt(timePeriod),
        compare_previous: true
      });
      setTrendsData(response.data.trends);
    } catch (error) {
      console.error("Error fetching trends:", error);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchDashboard();
    fetchTrends();
  }, [fetchDashboard, fetchTrends]);

  // Run AI analysis
  const runAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await api.post("/ai-analysis/segments", {
        segment_ids: selectedSegments,
        analysis_type: "comprehensive",
        time_period_days: parseInt(timePeriod),
        include_recommendations: true
      });
      
      if (response.data.success) {
        setAnalysisResult(response.data);
        toast.success("Analysis complete!");
      } else {
        toast.error(response.data.error || "Analysis failed");
      }
    } catch (error) {
      toast.error("Failed to run analysis");
    } finally {
      setAnalyzing(false);
    }
  };

  const getTrendIcon = (change) => {
    if (change > 0) return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (change < 0) return <ArrowDown className="h-4 w-4 text-red-500" />;
    return null;
  };

  const getTrendColor = (change) => {
    if (change > 0) return "text-green-600";
    if (change < 0) return "text-red-600";
    return "text-slate-600";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Brain className="h-8 w-8 text-purple-500" />
            AI CRM Insights
          </h1>
          <p className="text-muted-foreground">AI-powered analysis of your CRM data and customer segments</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Time period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="180">Last 6 months</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={runAnalysis} disabled={analyzing}>
            {analyzing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Run AI Analysis
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Contacts</p>
                <p className="text-2xl font-bold">{dashboardData?.quick_stats?.total_contacts || 0}</p>
              </div>
              <Users className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Leads</p>
                <p className="text-2xl font-bold">{dashboardData?.quick_stats?.total_leads || 0}</p>
              </div>
              <Target className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Deals</p>
                <p className="text-2xl font-bold">{dashboardData?.quick_stats?.total_deals || 0}</p>
              </div>
              <DollarSign className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Segments</p>
                <p className="text-2xl font-bold">{dashboardData?.quick_stats?.total_segments || 0}</p>
              </div>
              <PieChart className="h-8 w-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Trends */}
      {trendsData && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Key Trends (Last {timePeriod} Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6">
              {trendsData.revenue && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Revenue</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">${(trendsData.revenue.current || 0).toLocaleString()}</p>
                    {trendsData.revenue.change_percent !== null && (
                      <div className={`flex items-center ${getTrendColor(trendsData.revenue.change_percent)}`}>
                        {getTrendIcon(trendsData.revenue.change_percent)}
                        <span className="text-sm font-medium">{Math.abs(trendsData.revenue.change_percent)}%</span>
                      </div>
                    )}
                  </div>
                  {trendsData.revenue.previous !== null && (
                    <p className="text-xs text-muted-foreground mt-1">
                      vs ${trendsData.revenue.previous.toLocaleString()} previous period
                    </p>
                  )}
                </div>
              )}
              
              {trendsData.lead_count && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">New Leads</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{trendsData.lead_count.current || 0}</p>
                    {trendsData.lead_count.change_percent !== null && (
                      <div className={`flex items-center ${getTrendColor(trendsData.lead_count.change_percent)}`}>
                        {getTrendIcon(trendsData.lead_count.change_percent)}
                        <span className="text-sm font-medium">{Math.abs(trendsData.lead_count.change_percent)}%</span>
                      </div>
                    )}
                  </div>
                  {trendsData.lead_count.previous !== null && (
                    <p className="text-xs text-muted-foreground mt-1">
                      vs {trendsData.lead_count.previous} previous period
                    </p>
                  )}
                </div>
              )}
              
              {trendsData.conversion_rate && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground mb-1">Conversion Rate</p>
                  <div className="flex items-center gap-2">
                    <p className="text-2xl font-bold">{trendsData.conversion_rate.current || 0}%</p>
                    {trendsData.conversion_rate.change_points !== null && (
                      <div className={`flex items-center ${getTrendColor(trendsData.conversion_rate.change_points)}`}>
                        {getTrendIcon(trendsData.conversion_rate.change_points)}
                        <span className="text-sm font-medium">{Math.abs(trendsData.conversion_rate.change_points)} pts</span>
                      </div>
                    )}
                  </div>
                  {trendsData.conversion_rate.previous !== null && (
                    <p className="text-xs text-muted-foreground mt-1">
                      vs {trendsData.conversion_rate.previous}% previous period
                    </p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Segments for Analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Select Segments for Analysis</CardTitle>
          <CardDescription>Choose segments to include in the AI analysis (leave empty for all)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {segments.map((seg) => (
              <Badge
                key={seg.id}
                variant={selectedSegments.includes(seg.id) ? "default" : "outline"}
                className="cursor-pointer"
                onClick={() => {
                  if (selectedSegments.includes(seg.id)) {
                    setSelectedSegments(selectedSegments.filter(s => s !== seg.id));
                  } else {
                    setSelectedSegments([...selectedSegments, seg.id]);
                  }
                }}
              >
                {seg.label || seg.name}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* AI Analysis Results */}
      {analysisResult && analysisResult.ai_analysis && (
        <div className="space-y-4">
          {/* Executive Summary */}
          {analysisResult.ai_analysis.executive_summary && (
            <Card className="border-purple-200 bg-purple-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <Brain className="h-5 w-5" />
                  Executive Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-purple-800">{analysisResult.ai_analysis.executive_summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Performance Analysis */}
          {analysisResult.ai_analysis.performance_analysis && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Performance Analysis
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysisResult.ai_analysis.performance_analysis.strengths && (
                    <div className="p-4 bg-green-50 rounded-lg">
                      <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-1">
                        <CheckCircle2 className="h-4 w-4" />
                        Strengths
                      </h4>
                      {Array.isArray(analysisResult.ai_analysis.performance_analysis.strengths) ? (
                        <ul className="text-sm text-green-600 space-y-1">
                          {analysisResult.ai_analysis.performance_analysis.strengths.map((s, i) => (
                            <li key={i}>• {s}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-green-600">{analysisResult.ai_analysis.performance_analysis.strengths}</p>
                      )}
                    </div>
                  )}
                  
                  {analysisResult.ai_analysis.performance_analysis.weaknesses && (
                    <div className="p-4 bg-orange-50 rounded-lg">
                      <h4 className="font-semibold text-orange-700 mb-2 flex items-center gap-1">
                        <AlertTriangle className="h-4 w-4" />
                        Areas for Improvement
                      </h4>
                      {Array.isArray(analysisResult.ai_analysis.performance_analysis.weaknesses) ? (
                        <ul className="text-sm text-orange-600 space-y-1">
                          {analysisResult.ai_analysis.performance_analysis.weaknesses.map((w, i) => (
                            <li key={i}>• {w}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-orange-600">{analysisResult.ai_analysis.performance_analysis.weaknesses}</p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Segment Insights */}
          {analysisResult.ai_analysis.segment_insights && analysisResult.ai_analysis.segment_insights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5" />
                  Segment Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {analysisResult.ai_analysis.segment_insights.map((seg, idx) => (
                    <div key={idx} className="p-4 border rounded-lg">
                      <h4 className="font-semibold mb-2">{seg.segment_name}</h4>
                      {seg.insights && (
                        <p className="text-sm text-muted-foreground mb-2">{seg.insights}</p>
                      )}
                      {seg.opportunities && (
                        <div className="flex items-start gap-1 text-sm text-green-600">
                          <Lightbulb className="h-4 w-4 mt-0.5" />
                          <span>{seg.opportunities}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Geographic Insights */}
          {analysisResult.ai_analysis.geographic_insights && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Geographic Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {analysisResult.ai_analysis.geographic_insights.top_regions && (
                    <div>
                      <h4 className="font-semibold mb-2">Top Regions</h4>
                      <p className="text-sm text-muted-foreground">
                        {Array.isArray(analysisResult.ai_analysis.geographic_insights.top_regions)
                          ? analysisResult.ai_analysis.geographic_insights.top_regions.join(", ")
                          : analysisResult.ai_analysis.geographic_insights.top_regions
                        }
                      </p>
                    </div>
                  )}
                  {analysisResult.ai_analysis.geographic_insights.expansion_opportunities && (
                    <div>
                      <h4 className="font-semibold mb-2">Expansion Opportunities</h4>
                      <p className="text-sm text-muted-foreground">
                        {analysisResult.ai_analysis.geographic_insights.expansion_opportunities}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recommendations */}
          {analysisResult.ai_analysis.recommendations && analysisResult.ai_analysis.recommendations.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Lightbulb className="h-5 w-5" />
                  AI Recommendations
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analysisResult.ai_analysis.recommendations.map((rec, idx) => (
                    <div key={idx} className="p-4 bg-blue-50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-semibold text-blue-800">{rec.title}</h4>
                        <div className="flex items-center gap-2">
                          {rec.priority && (
                            <Badge variant={rec.priority === "high" ? "destructive" : rec.priority === "medium" ? "default" : "secondary"}>
                              {rec.priority}
                            </Badge>
                          )}
                          {rec.target_team && (
                            <Badge variant="outline">{rec.target_team}</Badge>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-blue-700">{rec.description}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Trends */}
          {analysisResult.ai_analysis.trends && analysisResult.ai_analysis.trends.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Identified Trends
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {analysisResult.ai_analysis.trends.map((trend, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-3 bg-slate-50 rounded">
                      <Clock className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div>
                        <p className="text-sm">{trend.description}</p>
                        {trend.impact && (
                          <p className="text-xs text-muted-foreground mt-1">Impact: {trend.impact}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* No Analysis Yet */}
      {!analysisResult && (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <Brain className="h-16 w-16 mx-auto mb-4 text-purple-300" />
            <h3 className="font-semibold text-lg mb-2">Ready for AI Analysis</h3>
            <p className="text-muted-foreground mb-4 max-w-md mx-auto">
              Click &quot;Run AI Analysis&quot; to get AI-powered insights about your customer segments, 
              performance trends, and actionable recommendations.
            </p>
            <Button onClick={runAnalysis} disabled={analyzing}>
              <Sparkles className="h-4 w-4 mr-2" />
              Run AI Analysis
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIInsightsPage;
